/*global define*/
define('core/services/UtilService',['angular', 'MapboxGL'], function(angular, mapboxgl) {
    'use strict';
    return angular.module('dnaApp').service('UtilService', ['$q', '$log', 'CacheFactory', '$http', '$window', '$rootScope', 'I18nService', '$location', 'ConfigurationService',
        function($q, $log, CacheFactory, $http, $window, $rootScope, I18nService, $location, ConfigurationService) {
            var currentTitlePromise;
            var utilServiceCache = CacheFactory('utilServiceCache ', {maxAge: 3600000}); // Keep items for only 1 hours

            /**
             * @method setPageInfoTags
             * @desc Set the title, description, and canonical tags of the DOM if this was the last request to be made.
             * @param titlePromise The promise that was used to fetch the title string from i18n.
             * @param title The actual title text.
             * @param description The description text.
             * @param path The path to be used in the canonical tag.
             * @private
             */
            function setPageInfoTags(titlePromise, title, description, path) {

                // We only want to act on the last request that was made. Just in case we make two
                // requests and the first request comes back after the second one.
                if (titlePromise === currentTitlePromise) {
                    $rootScope.pageInfo = {
                        title: title,
                        description: description
                    };
                    if (path) {
                        $rootScope.pageInfo.canonical = dna.app.baseUrl + path;
                    }
                }
            }

            function cacheContainsValue(key, value) {
                var cachedValue = utilServiceCache.get(key);
                return (cachedValue instanceof Array && cachedValue.length > 1 && cachedValue[1] === value);
            }

            function userIsInMatchCompareRollout() {
                var filterValues = ConfigurationService.configSettings.matchCompareRolloutUserFilter;
                if( filterValues ) {
                    filterValues = String(filterValues);
                    var ucdmId = $rootScope.Cookies.find('LAU');
                    return ucdmId && filterValues.length > 0 && filterValues.indexOf(ucdmId.toLowerCase().substr(7,1)) > -1;
                } else {
                    return true;
                }
            }

            /**
             * @class UtilService
             * @desc Service that provides general utility functions.
             */
            return {
                testSubjectCache: CacheFactory('testSubjectCache', {
                    maxAge: 3600000 // Keep items for only 1 hour
                }),

                getLocalStorage: function (key) {
                    try {
                        return localStorage.getItem(key);
                    } catch (e) {
                        return false;
                    }
                },

                setLocalStorage: function (key, value) {
                    try {
                        return localStorage.setItem(key, value);
                    } catch (e) {
                        return false;
                    }
                },

                /**
                 * @method assignWindowLocation
                 * @desc This method will call $window.location.assign to transfer control to another stack. We provide this
                 * method in a service so that it can be mocked for unit testing.
                 * @param url The new URL where we want to go.
                 */
                assignWindowLocation: function(url) {
                    $window.location.assign(url);
                },

                reloadPage: function() {
                    $window.location.reload();
                },

                /**
                 * @method trim
                 * @desc Strips any whitespace from the beginning and end of a string.
                 * @param {String} str The string from which to strip whitespace.
                 * @returns {String} The trimmed string.
                 * @public
                 */
                trim: function(str) {
                    if (str) {
                        return str.replace(/^\s\s*/, '').replace(/\s\s*$/, '');
                    }
                    return str;
                },

                /**
                 * @method isUserLoggedIn
                 * @desc Check to see if the user is logged in or not.
                 * @returns {Promise} The promise to be resolved or rejected.
                 * @public
                 */
                isUserLoggedIn: function() {
                    var deferred = $q.defer();
                    var url = '/public/is-user-logged-in';
                    $http.get(url).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(data, status, headers, config) {
                        deferred.reject(data, status, headers, config);
                    });
                    return deferred.promise;
                },

                /**
                 * @method getTestSubject
                 * @desc Get the test subject name
                 * @param testGuid
                 * @public
                 */
                getTestSubject: function(testGuid, shareToken1, shareToken2) {
                    var url = '/secure/tests/' + encodeURIComponent(testGuid) + '/testSubjectName/';

                    // Check for Shared state
                    if (shareToken1 && shareToken2 && shareToken1 !== 'preview' && shareToken2 !== 'preview') {
                        url = '/public/ethnicity/' + testGuid + '/share/' + shareToken1 + '/' + shareToken2 + '/testSubjectName/';
                    }

                    var deferred = $q.defer();

                    var cachedTestSubject = this.testSubjectCache.get(url);
                    if (cachedTestSubject) {
                        deferred.resolve({
                            testSubjectName: cachedTestSubject
                        });
                    } else {
                        $http.get(url).success(function(data, status, headers, config) {
                            deferred.resolve({
                                testSubjectName: data
                            }, status, headers, config);
                        }).error(function(data, status, headers, config) {
                            deferred.reject(data, status, headers, config);
                        });
                    }

                    return deferred.promise;
                },

                /**
                 * @method getUserTestMessageValue
                 * @desc get value of a specific message type. This is for messages that do not require a test guid.
                 * @param messageType One of the enum values in UserTestMessageType.java
                 */
                getUserTestMessageValue: function(messageType) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/value';
                    var deferred = $q.defer();
                    $http({
                        method: 'GET',
                        url: url,
                        headers: {Accept: 'text/plain'},
                        cache: utilServiceCache
                    }).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(data, status, headers, config) {
                        deferred.reject(data);
                    });

                    return deferred.promise;
                },

                /**
                 * @method setUserTestMessageValue
                 * @desc set the value of a specific message type. This is for messages that do not require a test guid.
                 * @param messageType One of the enum values in UserTestMessageType.java
                 * @param value The new value of the user test message.
                 */
                setUserTestMessageValue: function(messageType, value) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/value';
                    var deferred = $q.defer();
                    if (cacheContainsValue(url, value)) {
                        deferred.resolve(value);
                    } else {
                        $http({method: 'POST', url: url, headers: {'Content-Type': 'text/plain'}, data: value}).success(function() {
                            // Remove the cached value, if any, since we just changed it.
                            utilServiceCache.remove(url);
                            deferred.resolve(value);
                        }).error(function(errorReason) {
                            deferred.reject(errorReason);
                        });
                    }

                    return deferred.promise;
                },

                /**
                 * @method isUserTestMessageNotExpired
                 * @desc Determine if a given user message is expired. The message is defined by the message type and
                 * the test guid.
                 * @param messageType Must be one of the types in UserTestMessageType.java
                 * @param testGuid The test for which we are checking this message. These message are unique for user and test.
                 * @param days How many days from the first time the user sees this message does it expire?
                 * @returns {promise} True if the message is not yet expired, false if it has expired.
                 */
                isUserTestMessageNotExpired: function(messageType, testGuid, days) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/tests/' + encodeURIComponent(testGuid) + '/days/' + days;
                    var deferred = $q.defer();

                    $http.get(url).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(reason) {
                        deferred.reject(reason);
                    });
                    return deferred.promise;
                },

                /**
                 * @method isUserMessageNotExpired
                 * @desc Determine if a given user message is expired. The message is defined by the message type and
                 * the test guid.
                 * @param messageType Must be one of the types in UserTestMessageType.java
                 * @param days How many days from the first time the user sees this message does it expire?
                 * @returns {promise} True if the message is not yet expired, false if it has expired.
                 */
                isUserMessageNotExpired: function(messageType, days) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/days/' + days;
                    var deferred = $q.defer();

                    $http.get(url).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(reason) {
                        deferred.reject(reason);
                    });
                    return deferred.promise;
                },

                /**
                 * @method
                 * @desc Set the expired flag of the specified user test message.
                 * @param messageType Must be one of the types in UserTestMessageType.java
                 * @param testGuid The test for which we are setting the flag. These messages are unique for user and test.
                 * @param days Not actually used. Included for symmetry with the isUserTextMessageNotExpired method.
                 * @returns {promise}
                 */
                setUserTestMessageExpired: function(messageType, testGuid, days) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/tests/' + encodeURIComponent(testGuid) + '/days/' + days;
                    var deferred = $q.defer();

                    $http.post(url, {expired: true}).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(reason) {
                        deferred.reject(reason);
                    });
                    return deferred.promise;
                },

                /**
                 * @method
                 * @desc Set the expired flag of the specified user test message.
                 * @param messageType Must be one of the types in UserTestMessageType.java
                 * @param days Not actually used. Included for symmetry with the isUserTextMessageNotExpired method.
                 * @returns {promise}
                 */
                setUserMessageExpired: function(messageType, days) {

                    var url = '/secure/configuration/userTestMessages/' + encodeURIComponent(messageType) + '/days/' + days;
                    var deferred = $q.defer();

                    $http.post(url, {expired: true}).success(function(data) {
                        deferred.resolve(data);
                    }).error(function(reason) {
                        deferred.reject(reason);
                    });
                    return deferred.promise;
                },

                /**
                 * @method setPageInfo
                 * @desc Set the title, description, and canonical tags for the page.
                 * @param propertyName The name used to retrieve the title and description text.
                 * @param path What path to use in the canonical meta tag. If null, no meta will be shown.
                 * @public
                 */
                setPageInfo: function(propertyName, path) {

                    var titlePromise = I18nService.getViaPromise(propertyName + '.page.info.title');
                    var descriptionPromise = I18nService.getViaPromise(propertyName + '.page.info.description');
                    currentTitlePromise = titlePromise;
                    $q.all([titlePromise, descriptionPromise]).then(
                            function(data) {
                                setPageInfoTags(titlePromise, data[0], data[1], path);
                            },
                            function(errorReason) {
                                setPageInfoTags(titlePromise, 'AncestryDNA', 'Find your roots with AncestryDNA.', path);
                            });
                },

                /**
                 * isMobileDevice
                 * @desc determine if the current request came from a mobile device.  Uses detectmobilebrowsers.com regex
                 * to look at the userAgent from navigator.  Should not detect tablets.
                 * @returns {boolean}
                 */
                isMobileRequest: function() {
                    var ua = navigator.userAgent;
                    if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|ipod/i.test(ua)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(ua.substr(0,4))) {
                        return true;
                    } else {
                        return false;
                    }
                },
                /**
                 * Should detect mobile or tablet.
                 * @returns {boolean}
                 */
                isMobileOrTabletRequest: function() {
                    var ua = navigator.userAgent;
                    if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|ipod|playbook|silk/i.test(ua)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(ua.substr(0,4))) {
                        return true;
                    } else {
                        return false;
                    }
                },

                matchCompareIsAvailable: function() {
                    return ConfigurationService.configSettings.enableMatchCompare
                        && userIsInMatchCompareRollout()
                        && dna.app.isMainSite
                        && $window.innerWidth <= 600;
                },

                getMapboxGL: function () {
                    return mapboxgl;
                },

                getCurrentUcdmId: function() {
                    if( $rootScope.Cookies ) {
                        return $rootScope.Cookies.find('LAU');
                    } else {
                        return null;
                    }
                }

            };
        }]);
});

